import React, { useMemo } from 'react';
import type { Athlete } from '../types';
import type { LapCrossing, OffsetAdjustment } from '../services/raceAnalysis';

interface RaceLapStatsProps {
  athletes: Athlete[];
  raceLapsData: Record<number, LapCrossing[]>;
  dynamicOffsets: Map<string, OffsetAdjustment[]>;
}

const formatTimeGap = (ms: number): string => {
  if (ms <= 0) return '—';
  const totalSeconds = ms / 1000;
  if (totalSeconds < 60) {
      return `+${totalSeconds.toFixed(1)}s`;
  }
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `+${minutes}:${Math.round(seconds).toString().padStart(2, '0')}`;
};

const RaceLapStats: React.FC<RaceLapStatsProps> = ({ athletes, raceLapsData, dynamicOffsets }) => {
    // Apply dynamic offsets to the race lap data
    const adjustedRaceLapsData = useMemo(() => {
        const newData: Record<number, LapCrossing[]> = {};

        Object.entries(raceLapsData).forEach(([lapNumStr, crossings]: [string, LapCrossing[]]) => {
            const lapNum = parseInt(lapNumStr, 10);

            crossings.forEach(crossing => {
                // Find the offset valid at the time of this crossing
                const athleteAdjustments = dynamicOffsets.get(crossing.athleteId) || [];
                let offset = 0;
                
                // Iterate adjustments to find the one active at crossingTime
                for (let i = athleteAdjustments.length - 1; i >= 0; i--) {
                    if (athleteAdjustments[i].timestamp <= crossing.crossingTime) {
                        offset = athleteAdjustments[i].lapOffset;
                        break;
                    }
                }

                const adjustedLapNum = lapNum + offset;

                if (!newData[adjustedLapNum]) {
                    newData[adjustedLapNum] = [];
                }
                newData[adjustedLapNum].push(crossing);
            });
        });

        // Sort each lap by crossing time to determine position
        Object.values(newData).forEach(list => {
            list.sort((a, b) => a.crossingTime - b.crossingTime);
        });

        return newData;
    }, [raceLapsData, dynamicOffsets]);

    const lapNumbers = Object.keys(adjustedRaceLapsData).map(Number).sort((a, b) => a - b);

    if (athletes.length === 0) {
        return (
            <div className="flex items-center justify-center h-full text-gray-500">
                No athlete data to analyze race laps.
            </div>
        );
    }
    
    if (lapNumbers.length === 0) {
        return (
             <div className="flex items-center justify-center h-full text-gray-500">
                No complete laps detected to show race stats.
            </div>
        )
    }

    return (
        <div className="w-full flex flex-col text-sm pr-2 -mr-2">
            {lapNumbers.map(lapNum => {
                const lapCrossings = adjustedRaceLapsData[lapNum];
                if (!lapCrossings || lapCrossings.length === 0) return null;
                
                const leaderTime = lapCrossings[0].crossingTime;
                const top5 = lapCrossings.slice(0, 5);

                return (
                    <div key={lapNum} className="mb-4">
                        <h4 className="font-bold text-base text-cyan-400 border-b border-gray-700 pb-1 mb-2">
                            Finish of Lap {lapNum}
                        </h4>
                        <table className="w-full text-left table-fixed">
                            <thead>
                                <tr className="text-xs text-gray-400 uppercase">
                                    <th className="p-1 w-[15%]">Pos</th>
                                    <th className="p-1 w-[55%]">Rider</th>
                                    <th className="p-1 w-[30%]">Time Gap</th>
                                </tr>
                            </thead>
                            <tbody>
                                {top5.map((crossing, index) => (
                                    <tr key={crossing.athleteId} className="border-t border-gray-700 hover:bg-gray-700/50">
                                        <td className="p-1.5 font-semibold">{index + 1}</td>
                                        <td className="p-1.5 truncate">{crossing.athleteName}</td>
                                        <td className="p-1.5">
                                            {index === 0 ? 'Leader' : formatTimeGap(crossing.crossingTime - leaderTime)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                );
            })}
        </div>
    );
};

export default RaceLapStats;